/**
 * useLeadOrder — live snapshot of a single lead order.
 *
 * Reads the `?o=` order id from the URL and subscribes to
 * clients/{effectiveClientId}/leadOrders/{orderId} (functions-only writes;
 * the client only ever reads). The ConfigureOrder page renders off `status`.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useEffectiveClientId } from '../../hooks/useImpersonation';

export type LeadOrderStatus =
  | 'draft'
  | 'awaiting_payment'
  | 'paid'
  | 'generating'
  | 'fulfilled'
  | 'error'
  | 'cancelled';

export interface LeadOrderItem {
  productId?: string;
  /** '$5' | '.50' | '.25' | '.10' | '.05' */
  pricePoint: string;
  qty: number;
  unitPrice?: number;
}

export interface LeadOrderFile {
  pricePoint: string;
  fileName: string;
  gcsPath?: string;
  requested?: number;
  delivered?: number;
  runId?: string;
  injectedCount?: number;
  dupSkipped?: number;
}

export interface LeadOrder {
  status: LeadOrderStatus;
  items: LeadOrderItem[];
  files: LeadOrderFile[];
  deliveryEmail: string | null;
  amountUsd?: number;
  error?: { step?: string; message?: string } | null;
}

interface UseLeadOrderResult {
  orderId: string | null;
  order: LeadOrder | null;
  loading: boolean;
  /** false = no order id, doc missing, or read denied. Drives redirect-home. */
  exists: boolean;
}

export function useLeadOrder(): UseLeadOrderResult {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('o');
  const effectiveClientId = useEffectiveClientId();

  const [order, setOrder] = useState<LeadOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(true);

  useEffect(() => {
    if (!orderId || !effectiveClientId) {
      setOrder(null);
      setExists(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ref = doc(db, 'clients', effectiveClientId, 'leadOrders', orderId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setOrder({
            status: (data.status as LeadOrderStatus) || 'draft',
            items: Array.isArray(data.items) ? (data.items as LeadOrderItem[]) : [],
            files: Array.isArray(data.files) ? (data.files as LeadOrderFile[]) : [],
            deliveryEmail: (data.deliveryEmail as string | null) ?? null,
            amountUsd: typeof data.amountUsd === 'number' ? data.amountUsd : undefined,
            error: (data.error as LeadOrder['error']) ?? null,
          });
          setExists(true);
        } else {
          setOrder(null);
          setExists(false);
        }
        setLoading(false);
      },
      (err) => {
        console.error('leadOrder listener error:', err);
        setOrder(null);
        setExists(false);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [orderId, effectiveClientId]);

  return { orderId, order, loading, exists };
}

/**
 * Retail file-name preview — mirrors the engine's build_retail_filename:
 *   f"{count:,} {price_point} Leads {d.month}-{d.day}-{d.strftime('%y')}.xlsx"
 * e.g. buildRetailFilename(1000, '.50') -> "1,000 .50 Leads 7-14-26.xlsx"
 * Month/day carry no leading zeros; year is 2 digits. Uses today's date.
 */
export function buildRetailFilename(count: number, pricePoint: string, d: Date = new Date()): string {
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  return `${count.toLocaleString('en-US')} ${pricePoint} Leads ${month}-${day}-${yy}.xlsx`;
}
